import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import MobileDialog from './MobileDialog';
import { useUser } from '../../contexts/UserContext';
import API from '../../services/api';
import {
  renderBarcodeSVG,
  printBarcodeLabels,
  isPrintableBarcode,
} from '../../utils/barcodePrintUtils';

const UNSUPPORTED_MSG =
  'ניתן להדפיס רק ברקודים מסוג EAN-13, EAN-8, ITF-14 או Code 128';

/**
 * Dialog that lets the user search for products and print thermal barcode
 * labels. Opens EMPTY — user searches and adds products one at a time.
 *
 * Props:
 *   - open, onClose
 *   - initialProducts: optional array to pre-populate (e.g. from GenerateBarcodeDialog)
 */
const BarcodePrintDialog = ({ open, onClose, initialProducts = [] }) => {
  const { userProfile } = useUser();
  const preferredSymbology = userProfile?.group?.barcode_config?.print_symbology;
  const [items, setItems] = useState([]);
  const [showPrice, setShowPrice] = useState('with');
  const [error, setError] = useState('');
  const previewSvgRef = useRef(null);

  // Autocomplete search state
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  // Seed items from initialProducts when dialog opens.
  // If no initialProducts, starts empty (search-and-select mode).
  useEffect(() => {
    if (open) {
      setError('');
      setSearchInput('');
      setSearchOptions([]);
      if (initialProducts && initialProducts.length > 0) {
        setItems(
          initialProducts
            .filter((p) => p && p.Barcode)
            .map((p) => ({
              ProductID: p.ProductID,
              ProductName: p.ProductName,
              Barcode: p.Barcode,
              Price: p.Price,
              labelCount: 1,
            }))
        );
      } else {
        setItems([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalLabels = useMemo(
    () => items.reduce((sum, it) => sum + (parseInt(it.labelCount || 0, 10) || 0), 0),
    [items]
  );

  const unprintableItems = useMemo(
    () => items.filter((it) => !isPrintableBarcode(it.Barcode)),
    [items]
  );

  const previewItem = useMemo(
    () => items.find((it) => isPrintableBarcode(it.Barcode)) || null,
    [items]
  );

  // Preview renders the first printable item as a representative sample.
  useEffect(() => {
    if (open && previewSvgRef.current && previewItem) {
      try {
        renderBarcodeSVG(previewSvgRef.current, previewItem.Barcode, {
          height: 60,
          width: 2,
          fontSize: 14,
          preferredSymbology,
        });
      } catch {
        // Pre-flight above guarantees this won't fire, but leave a silent
        // guard so a future unsupported symbology doesn't crash the dialog.
      }
    }
  }, [open, previewItem, showPrice, preferredSymbology]);

  // Debounced product search
  useEffect(() => {
    if (!searchInput || searchInput.length < 2) {
      setSearchOptions([]);
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await API.get('/products/', { params: { ts: Date.now() } });
        const all = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        const q = searchInput.toLowerCase();
        const filtered = all
          .filter(
            (p) =>
              p.Barcode &&
              !items.find((it) => it.ProductID === p.ProductID) &&
              ((p.ProductName || '').toLowerCase().includes(q) ||
               (p.CatalogNumber || '').toLowerCase().includes(q) ||
               (p.Barcode || '').includes(q))
          )
          .slice(0, 15);
        setSearchOptions(filtered);
      } catch {
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchInput, items]);

  const addProduct = useCallback((product) => {
    if (!product?.Barcode) return;
    setItems((prev) => {
      if (prev.find((it) => it.ProductID === product.ProductID)) return prev;
      return [
        ...prev,
        {
          ProductID: product.ProductID,
          ProductName: product.ProductName,
          Barcode: product.Barcode,
          Price: product.Price,
          labelCount: 1,
        },
      ];
    });
    setSearchInput('');
    setSearchOptions([]);
  }, []);

  const updateCount = (productId, delta) => {
    setItems((prev) =>
      prev.map((it) =>
        it.ProductID === productId
          ? { ...it, labelCount: Math.max(0, (parseInt(it.labelCount || 0, 10) || 0) + delta) }
          : it
      )
    );
  };

  const setCount = (productId, value) => {
    const v = parseInt(value || '0', 10);
    setItems((prev) =>
      prev.map((it) =>
        it.ProductID === productId
          ? { ...it, labelCount: Number.isNaN(v) ? 0 : Math.max(0, v) }
          : it
      )
    );
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((it) => it.ProductID !== productId));
  };

  const handlePrint = () => {
    setError('');
    if (totalLabels === 0) {
      setError('בחר לפחות מדבקה אחת להדפסה');
      return;
    }
    if (unprintableItems.length > 0) {
      setError(UNSUPPORTED_MSG);
      return;
    }
    try {
      printBarcodeLabels(
        items.map((it) => ({
          barcode: it.Barcode,
          productName: it.ProductName,
          price: it.Price,
          labelCount: it.labelCount,
        })),
        { showPrice: showPrice === 'with', preferredSymbology }
      );
    } catch (e) {
      setError(e?.message || 'שגיאה בהדפסה');
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon color="primary" />
          <Typography variant="h6">הדפסת מדבקות ברקוד</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {unprintableItems.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {UNSUPPORTED_MSG}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {unprintableItems.map((it) => it.ProductName).join(', ')}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left: search + items list */}
          <Box sx={{ flex: 2, minWidth: 0 }}>
            {/* Product search */}
            <Autocomplete
              freeSolo
              options={searchOptions}
              getOptionLabel={(o) => typeof o === 'string' ? o : `${o.ProductName} (${o.Barcode})`}
              inputValue={searchInput}
              onInputChange={(_, v) => setSearchInput(v)}
              onChange={(_, value) => { if (value && typeof value !== 'string') addProduct(value); }}
              loading={searchLoading}
              noOptionsText={searchInput.length < 2 ? 'הקלד לחיפוש...' : 'לא נמצאו מוצרים'}
              renderOption={(props, option) => (
                <li {...props} key={option.ProductID}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2">{option.ProductName}</Typography>
                    <Typography variant="caption" sx={{ direction: 'ltr', fontFamily: 'monospace', color: 'text.secondary' }}>
                      {option.Barcode}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="חפש מוצר להוספה..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 0.5 }} />,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              מוצרים ({items.length})
            </Typography>
            {items.length === 0 ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  border: '2px dashed #e0e0e0',
                  borderRadius: 2,
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">חפש מוצר והוסף אותו לרשימת ההדפסה</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                {items.map((it) => (
                  <Box
                    key={it.ProductID}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                      px: 0.5,
                      borderBottom: '1px solid #f1f5f9',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    {/* Product info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap fontWeight={500}>
                        {it.ProductName}
                      </Typography>
                      <Typography variant="caption" sx={{ direction: 'ltr', fontFamily: 'monospace', color: 'text.secondary' }}>
                        {it.Barcode}
                      </Typography>
                      {it.Price != null && (
                        <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          ₪{Number(it.Price).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    {/* Quantity controls - fixed alignment */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => updateCount(it.ProductID, -1)}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <TextField
                        size="small"
                        value={it.labelCount}
                        onChange={(e) => setCount(it.ProductID, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onWheel={(e) => e.target.blur()}
                        inputProps={{
                          style: { textAlign: 'center', width: 32, padding: '4px 0' },
                          'aria-label': 'label count',
                        }}
                        sx={{
                          width: 48,
                          '& input': {
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            MozAppearance: 'textfield',
                          },
                        }}
                      />
                      <IconButton size="small" onClick={() => updateCount(it.ProductID, 1)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeItem(it.ProductID)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

          {/* Right: sticky preview */}
          <Box sx={{ flex: 1, minWidth: { xs: 0, md: 220 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              תצוגה מקדימה
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel sx={{ fontSize: '0.85rem' }}>הצגת מחיר</FormLabel>
              <RadioGroup
                row
                value={showPrice}
                onChange={(e) => setShowPrice(e.target.value)}
              >
                <FormControlLabel value="with" control={<Radio size="small" />} label="עם מחיר" />
                <FormControlLabel value="without" control={<Radio size="small" />} label="ללא מחיר" />
              </RadioGroup>
            </FormControl>
            <Box
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: '#fff',
                textAlign: 'center',
                direction: 'ltr',
              }}
            >
              {previewItem ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, direction: 'rtl' }}>
                    {previewItem.ProductName}
                  </Typography>
                  {showPrice === 'with' && previewItem.Price != null && (
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      ₪{Number(previewItem.Price).toFixed(2)}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <svg ref={previewSvgRef} />
                  </Box>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {items.length === 0
                    ? 'הוסף מוצרים לראות תצוגה מקדימה'
                    : UNSUPPORTED_MSG}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              גודל מדבקה: 40×58 מ"מ
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={totalLabels === 0 || unprintableItems.length > 0}
          sx={{ direction: 'ltr' }}
        >
          הדפס {totalLabels} מדבקות
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default BarcodePrintDialog;
